import React from 'react';
import { Box, Button, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { alpha, darken, useTheme } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BoltIcon from '@mui/icons-material/Bolt';
import { motion, Variants } from 'framer-motion';
import { AMBER, HERO, PERSONA_DOORS_SECTION_ID, scrollToPersonaDoors } from '../demoContent';

interface DemoHeroProps {
  onStartTour: () => void;
  loading: boolean;
}

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const stagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09 } },
};

/** Decorative vignette data — generic names, product-shaped, no real people or numbers implied. */
const LEADERBOARD_ROWS = [
  { rank: 1, name: 'Ava R.', xp: '2,480 XP' },
  { rank: 2, name: 'Diego M.', xp: '2,310 XP' },
  { rank: 3, name: 'Priya S.', xp: '2,205 XP' },
];

function HeroVignette() {
  const theme = useTheme();

  return (
    <Box sx={{ position: 'relative', height: 400 }} aria-hidden>
      {/* Live leaderboard card */}
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.6, ease: 'easeOut' }}
        style={{ position: 'absolute', top: 48, left: 0, width: '72%', maxWidth: 320 }}
      >
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 24px 60px rgba(10, 14, 40, 0.45)' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: AMBER.main,
                  animation: 'demoHeroPulse 2.2s ease-out infinite',
                  '@keyframes demoHeroPulse': {
                    '0%': { boxShadow: `0 0 0 0 ${alpha(AMBER.main, 0.5)}` },
                    '70%': { boxShadow: `0 0 0 8px ${alpha(AMBER.main, 0)}` },
                    '100%': { boxShadow: `0 0 0 0 ${alpha(AMBER.main, 0)}` },
                  },
                }}
              />
              <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: 'text.secondary' }}>
                LIVE LEADERBOARD
              </Typography>
            </Stack>

            <Stack spacing={0.5}>
              {LEADERBOARD_ROWS.map(row => (
                <Stack
                  key={row.rank}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    py: 0.75,
                    px: 1,
                    borderRadius: 1.5,
                    bgcolor: row.rank === 1 ? alpha(AMBER.main, 0.14) : 'transparent',
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: row.rank === 1 ? AMBER.main : alpha(theme.palette.primary.main, 0.1),
                      color: row.rank === 1 ? AMBER.contrastText : 'primary.main',
                    }}
                  >
                    {row.rank}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {row.xp}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </motion.div>
      </motion.div>

      {/* Auto-graded pill */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.6, ease: 'easeOut' }}
        style={{ position: 'absolute', top: 0, right: 8 }}
      >
        <Paper
          sx={{
            px: 1.75,
            py: 1,
            borderRadius: 99,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            boxShadow: '0 14px 36px rgba(10, 14, 40, 0.38)',
          }}
        >
          <BoltIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Auto-graded · +40 XP
          </Typography>
        </Paper>
      </motion.div>

      {/* Badge unlocked card */}
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6, ease: 'easeOut' }}
        style={{ position: 'absolute', bottom: 48, right: 0, width: 250 }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Paper
            sx={{
              p: 2,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 18px 44px rgba(10, 14, 40, 0.4)',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(AMBER.main, 0.16),
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: 22, color: AMBER.main }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Badge unlocked
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Ten correct in a row
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </motion.div>
    </Box>
  );
}

export default function DemoHero({ onStartTour, loading }: DemoHeroProps) {
  const theme = useTheme();

  const handleExplore = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    scrollToPersonaDoors();
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: 'common.white',
        background: `linear-gradient(155deg, ${darken(theme.palette.primary.dark, 0.45)} 0%, ${
          theme.palette.primary.dark
        } 55%, ${theme.palette.primary.main} 100%)`,
      }}
    >
      {/* Fine dot grid, fading out toward the bottom of the band */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${alpha('#FFFFFF', 0.1)} 1px, transparent 1px)`,
          backgroundSize: '26px 26px',
          maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent 80%)',
          WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent 80%)',
        }}
      />
      {/* Amber glow behind the vignette */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: 560,
          height: 560,
          borderRadius: '50%',
          top: -160,
          right: -120,
          background: `radial-gradient(circle, ${alpha(AMBER.main, 0.2)} 0%, transparent 65%)`,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 9, md: 13 } }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <motion.div variants={stagger} initial="hidden" animate="visible">
              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 2.5,
                    textTransform: 'uppercase',
                    color: AMBER.main,
                    mb: 2,
                  }}
                >
                  {HERO.overline}
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.12,
                    maxWidth: 640,
                  }}
                >
                  {HERO.heading}
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    mt: 3,
                    fontWeight: 400,
                    lineHeight: 1.65,
                    maxWidth: 580,
                    color: alpha('#FFFFFF', 0.85),
                  }}
                >
                  {HERO.sub}
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                  <Button
                    data-demo-id="demo-start-tour"
                    size="large"
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={onStartTour}
                    disabled={loading}
                    sx={{
                      bgcolor: AMBER.main,
                      color: AMBER.contrastText,
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      '&:hover': { bgcolor: AMBER.dark },
                      '&.Mui-disabled': {
                        bgcolor: alpha(AMBER.main, 0.4),
                        color: alpha(AMBER.contrastText, 0.5),
                      },
                    }}
                  >
                    {loading ? HERO.primaryCtaLoading : HERO.primaryCta}
                  </Button>
                  <Button
                    size="large"
                    variant="outlined"
                    endIcon={<ArrowDownwardIcon />}
                    href={`#${PERSONA_DOORS_SECTION_ID}`}
                    onClick={handleExplore}
                    sx={{
                      color: 'common.white',
                      borderColor: alpha('#FFFFFF', 0.5),
                      px: 3,
                      '&:hover': { borderColor: 'common.white', bgcolor: alpha('#FFFFFF', 0.08) },
                    }}
                  >
                    {HERO.secondaryCta}
                  </Button>
                </Stack>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ mt: 4, flexWrap: 'wrap', rowGap: 1 }}
                >
                  {HERO.assurances.map(assurance => (
                    <Stack key={assurance} direction="row" alignItems="center" spacing={0.75}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: AMBER.main }} />
                      <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.85) }}>
                        {assurance}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </motion.div>
            </motion.div>
          </Grid>

          <Grid item md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
            <HeroVignette />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
